import { FC, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { toRound } from '../../../../../utils/get-rounded-number.util';
import {
  CapTableTab,
  Column,
  ScenarioFundraisingCapTableRow,
  ShareClassColumns,
  StakeholderColumns,
  StakeholdersViewBy,
} from '../fundraising.types';
import { TableRow } from './table-row';

export type PoolTableItemProps = {
  fields:
    | Record<
        ShareClassColumns,
        {
          change: null | number | number[] | string;
          value: number | number[] | string | string[];
        } | null
      >
    | Record<
        StakeholderColumns,
        {
          change: null | number | number[] | string;
          value: number | number[] | string | string[];
        } | null
      >;
  items?: ({ id: string; name: string } & Pick<
    ScenarioFundraisingCapTableRow,
    | 'convertedBasis'
    | 'convertedBasisPercentage'
    | 'defaultShareClass'
    | 'diluted'
    | 'dilutedPercentage'
    | 'issued'
    | 'issuedPercentage'
    | 'shareClasses'
  >)[];
  selectedViewBy?: 'all' | StakeholdersViewBy;
  tab: CapTableTab;
  trClassName?: string;
  viewBy?: Record<StakeholdersViewBy, boolean>;
  visibleColumns: Column[];
};

export const EquityPoolsRow: FC<PoolTableItemProps> = ({
  visibleColumns,
  viewBy,
  items = [],
  tab,
  fields,
  selectedViewBy,
  trClassName,
}) => {
  const [isExpanded, setExpanded] = useState(false);
  const isItemsEmpty = items.length === 0;
  const commonProps = {
    handleExpandRow: setExpanded,
    isExpanded,
    isExpandedRow: isItemsEmpty,
    trClassName: twMerge(isExpanded && 'bg-brand-25'),
    visibleColumns,
    viewBy,
    selectedViewBy,
  };

  if (!items.length) return null;

  return (
    <>
      {tab === CapTableTab.BY_STAKEHOLDER ? (
        <TableRow
          {...commonProps}
          fields={
            fields as Record<
              StakeholderColumns,
              {
                change: null | number | number[] | string;
                value: number | number[] | string | string[];
              } | null
            >
          }
          isPool={true}
          tab={CapTableTab.BY_STAKEHOLDER}
          trClassName={twMerge(trClassName, isExpanded && 'bg-brand-25')}
        />
      ) : (
        <TableRow
          {...commonProps}
          fields={
            fields as Record<
              ShareClassColumns,
              {
                change: null | number | number[] | string;
                value: number | number[] | string | string[];
              } | null
            >
          }
          tab={CapTableTab.BY_SHARE_CLASS}
          trClassName={twMerge(trClassName, isExpanded && 'bg-brand-25')}
        />
      )}

      {isExpanded && (
        <>
          {items.map(
            (
              {
                name,
                convertedBasis,
                convertedBasisPercentage,
                defaultShareClass,
                diluted,
                dilutedPercentage,
                issued,
                issuedPercentage,
                shareClasses,
              },
              i,
            ) => {
              const commonProps = {
                className: 'bg-[#F9FAFB] shadow-[inset_2px_0px_0px_0px_#2565C8]',
                isExpandedRow: true,
                trClassName: 'bg-brand-25',
                viewBy,
                visibleColumns,
                selectedViewBy,
              };

              const fields = {
                [Column.NAME]: { value: name, change: null },
                [Column.STAKEHOLDER_NAME]: { value: name, change: null },
                [Column.ISSUED]: {
                  value: toRound(issuedPercentage.value * 100),
                  change: issuedPercentage.change,
                },
                [Column.DILUTED]: {
                  value: toRound(dilutedPercentage.value * 100),
                  change: dilutedPercentage.change,
                },
                [Column.NO_ISSUED]: issued,
                [Column.DILUTED_SHARES]: diluted,
                [Column.CONVERTED_SHARES]: convertedBasis,
                [Column.CONVERTED_PERCENTAGE]: {
                  value: toRound(convertedBasisPercentage.value * 100),
                  change: convertedBasisPercentage.change,
                },
                [Column.VOTING_RIGHTS]: { value: '-', change: null },
                [Column.INVESTMENT]: { value: '-', change: null },
                [Column.AVERAGE_SHARE_PRICE]: { value: '-', change: null },
                [Column.VOTING]: { value: '-', change: null },
                [Column.TYPE]:
                  selectedViewBy === StakeholdersViewBy.STAKEHOLDERS
                    ? { value: '-', change: null }
                    : { value: name, change: null },
                [Column.COMMON_STOCK]: defaultShareClass,
                [Column.OTHER_SHARE_CLASSES]: {
                  value: shareClasses.map(({ value }) => value),
                  change: shareClasses.map(({ change }) => change),
                },
                [Column.NO_OF_STAKEHOLDERS]: { value: '-', change: null },
              };

              if (tab === CapTableTab.BY_STAKEHOLDER) {
                return (
                  <TableRow
                    {...commonProps}
                    fields={
                      fields as Record<
                        StakeholderColumns,
                        {
                          change: null | number | number[] | string;
                          value: number | number[] | string | string[];
                        } | null
                      >
                    }
                    isPool={true}
                    key={`${i}_${name}`}
                    tab={CapTableTab.BY_STAKEHOLDER}
                  />
                );
              } else {
                return (
                  <TableRow
                    {...commonProps}
                    fields={
                      fields as Record<
                        ShareClassColumns,
                        {
                          change: null | number | number[] | string;
                          value: number | number[] | string | string[];
                        } | null
                      >
                    }
                    key={`${i}_${name}`}
                    tab={CapTableTab.BY_SHARE_CLASS}
                  />
                );
              }
            },
          )}
        </>
      )}
    </>
  );
};
